import {
  Checkbox,
  formatDate,
  formatSchoolYear,
  ListMenu,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@compass/core";

import { ListBeProperty } from "components";
import {
  LkStudentEnrollmentDisplayFieldId,
  Permission,
  SortObject,
  StudentEnrollmentListDto,
  StudentEnrollmentListResource,
} from "generated/openApi";
import { useNavigate } from "react-router-dom";
import { studentEnrollmentPageRoute } from "../StudentEnrollmentPage/StudentEnrollmentPage";
import { useStudentEnollmentListMenuItems } from "./useStudentEnollmentListMenuItems";

export const StudentEnrollmentTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  studentEnrollments: StudentEnrollmentListDto[];
  listTotal: number | undefined;
  resource: StudentEnrollmentListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const menuItems = useStudentEnollmentListMenuItems({ permission: props.rootPermission });
  const displayFields = props.resource.displayFields || [];
  const startDateDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.StartDate);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);
  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const endGradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.EndGradeLevel);
  const customIdType = props.resource.customIdTypes?.[0];

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}
            <TableCell type="id">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("customId1"))}
                displayName={customIdType?.name || "ID"}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("student"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("schoolYear"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>

            {startDateDisplayField && (
              <TableCell>
                <ListBeProperty
                  listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("startDate"))}
                  displayName={startDateDisplayField.displayName}
                  sortObjects={props.sortObjects}
                  onChange={(sortObjects) => props.sort(sortObjects)}
                  searchFocused={props.searchFocused}
                />
              </TableCell>
            )}

            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("endDate"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>

            {schoolDisplayField && (
              <TableCell>
                <ListBeProperty
                  listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("school"))}
                  displayName={schoolDisplayField.displayName}
                  sortObjects={props.sortObjects}
                  onChange={(sortObjects) => props.sort(sortObjects)}
                  searchFocused={props.searchFocused}
                />
              </TableCell>
            )}

            <TableCell>
              <ListBeProperty
                displayName={gradeLevelDisplayField?.displayName}
                listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("gradeLevel"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.studentEnrollments.map((studentEnrollmentItem) => (
            <TableRow
              key={studentEnrollmentItem.id}
              onClick={() =>
                navigate({
                  pathname: studentEnrollmentPageRoute.getPath({
                    id: studentEnrollmentItem.id,
                    crudMode: "view",
                    screenType: "standard",
                  }),
                })
              }
            >
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Checkbox
                    checked={props.selectedIds.includes(studentEnrollmentItem.id)}
                    onClick={(e) => props.selectId(studentEnrollmentItem.id)}
                  />
                </TableCell>
              )}
             <TableCell type="id">{studentEnrollmentItem.customId1}</TableCell>
              <TableCell>{studentEnrollmentItem.student}</TableCell>
              <TableCell>{formatSchoolYear(studentEnrollmentItem.schoolYear)}</TableCell>
              {startDateDisplayField && <TableCell>{formatDate(studentEnrollmentItem.startDate)}</TableCell>}
              <TableCell>{formatDate(studentEnrollmentItem.endDate)}</TableCell>
              {schoolDisplayField && <TableCell>{studentEnrollmentItem.school}</TableCell>}
              <TableCell>{studentEnrollmentItem.gradeLevel}</TableCell>
              <TableCell type="action">
                {/* {isAuthorized(props.rootPermission, PermissionAction.Update) &&
                  (!studentEnrollmentItem.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
                    <UpdateIconButton
                      onClick={() =>
                        navigate({
                          pathname: studentEnrollmentPageRoute.getPath({
                            id: studentEnrollmentItem.id,
                            crudMode: "edit",
                          }),
                          search: studentEnrollmentPageRoute.getOptionalParams?.({ dialog: false }),
                        })
                      }
                    />
                  )} */}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
